import { useMemo } from 'react';

import { type Assets } from '@air/api';

import { useIsFlattenedView } from '~/hooks/search/useIsFlattenedView';
import { useURLBoardIdSelector } from '~/hooks/useURLBoardIdSelector';

type PrivateAssetVersionViewParams = NonNullable<Parameters<typeof Assets.Versions.view>[0]>;
type RequestLocation<Request> = Request extends unknown
  ? Pick<Request, Extract<keyof Request, 'location' | 'boardId'>>
  : never;

export type PrivateAssetOpenLocation = RequestLocation<PrivateAssetVersionViewParams>;

/**
 * Resolves the backend analytics location for the private asset modal from the current route context.
 */
export const usePrivateAssetOpenLocation = (): PrivateAssetOpenLocation | null => {
  const boardId = useURLBoardIdSelector();
  const { isFlattenedView } = useIsFlattenedView();

  return useMemo<PrivateAssetOpenLocation | null>(() => {
    // Attribution is omitted when expanded results make the URL board an unreliable asset context.
    if (isFlattenedView) {
      return null;
    }

    // Direct board routes are attributed to their current board.
    if (boardId) {
      return { location: 'board', boardId };
    }

    // Supported non-board routes are grouped under the backend search location.
    return { location: 'search' };
  }, [boardId, isFlattenedView]);
};
